import { isPlatformBrowser } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  PLATFORM_ID,
  SimpleChanges
} from "@angular/core";
import { UserInterface } from "@core/interfaces/user.interface";
import { TranslateService } from "@ngx-translate/core";
import { PostQualityIndicatorType, UserPostQualityResponse } from "@shared/interfaces/user-post-quality.interface";
import { UserPostQualityApiService } from "@shared/services/user-post-quality-api.service";
import { Observable, of, Subject } from "rxjs";
import { catchError, map, takeUntil } from "rxjs/operators";

@Component({
  selector: "astrobin-user-post-quality-indicators",
  templateUrl: "./user-post-quality-indicators.component.html",
  styleUrls: ["./user-post-quality-indicators.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserPostQualityIndicatorsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() user: UserInterface;
  @Input() period = 90;
  @Input() showPeriodSelector = true;
  @Input() size: "small" | "medium" | "large" = "medium";
  @Input() layout: "horizontal" | "grid" = "horizontal";
  @Input() refreshTrigger$: Observable<void>;

  indicators$: Observable<PostQualityIndicatorType[]>;
  loading$: Observable<boolean>;
  error = false;

  get periodOptions() {
    return [
      { value: 7, label: this.translateService.instant("Last 7 days") },
      { value: 30, label: this.translateService.instant("Last 30 days") },
      { value: 90, label: this.translateService.instant("Last 90 days") },
      { value: 365, label: this.translateService.instant("Last year") }
    ];
  }

  private readonly isBrowser: boolean;
  private readonly destroyed$ = new Subject<void>();

  constructor(
    private readonly userPostQualityApiService: UserPostQualityApiService,
    private readonly translateService: TranslateService,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    if (this.refreshTrigger$) {
      this.refreshTrigger$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this._loadIndicators();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.user || changes.period) {
      this._loadIndicators();
    }
  }

  onPeriodChange(newPeriod: number): void {
    this.period = newPeriod;
    this._loadIndicators();
  }

  private _loadIndicators(): void {
    if (!this.user || !this.isBrowser) {
      this.indicators$ = of([]);
      return;
    }

    this.error = false;
    this.indicators$ = this.userPostQualityApiService.getUserPostQuality(this.user.id, this.period).pipe(
      map((response: UserPostQualityResponse) => {
        return Object.keys(response.badgeSummary) as PostQualityIndicatorType[];
      }),
      catchError(() => {
        this.error = true;
        return of([]);
      })
    );
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
